import React, { useState, useEffect, useRef } from 'react';
import { auth, db, functions } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, where, onSnapshot, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import PlanSelectionModal from './PlanSelectionModal';
import '../styles.css';

function Header({ syncStatus, subscriptionStatus }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showTrialBanner, setShowTrialBanner] = useState(false);
  const [daysLeft, setDaysLeft] = useState(0);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [shareNames, setShareNames] = useState({});
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  // Subscribe to in-app notifications for the signed-in user
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const q = query(collection(db, `users/${uid}/notifications`), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setNotifications(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('Notifications error:', err.message));
    return () => unsubscribe();
  }, [subscriptionStatus]);

  // Build a map of shared-user uid -> custom display name the user assigned,
  // so notifications can show names instead of email addresses.
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'shares'), where('ownerUid', '==', uid)));
        if (cancelled) return;
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.recipientUid && data.recipientName) map[data.recipientUid] = data.recipientName;
        });
        setShareNames(map);
      } catch (err) {
        console.error('Error loading share names:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [subscriptionStatus]);

  const senderName = (n) => (n.fromUid && shareNames[n.fromUid]) || n.fromEmail;

  const unreadCount = notifications.filter(n => !n.read).length;

  const openNotifications = async () => {
    const next = !showNotifs;
    setShowNotifs(next);
    if (next && unreadCount > 0) {
      const uid = auth.currentUser?.uid;
      const unread = notifications.filter(n => !n.read);
      await Promise.all(unread.map(n =>
        updateDoc(doc(db, `users/${uid}/notifications/${n.id}`), { read: true }).catch(() => {})
      ));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifs(false);
      }
    };
    if (showNotifs) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifs]);

  const handleNotifClick = (n) => {
    setShowNotifs(false);
    if (n.sessionId) {
      // Notifications go to the owner, so the session is in their own account.
      // Open the setup sheet for the referenced session.
      navigate(`/session-setup/${n.sessionId}`, {
        state: { vehicleId: n.vehicleId, eventId: n.eventId },
      });
    }
  };

  const formatNotifTime = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const clearNotification = async (e, n) => {
    e.stopPropagation();
    const uid = auth.currentUser?.uid;
    if (uid) await deleteDoc(doc(db, `users/${uid}/notifications/${n.id}`)).catch(() => {});
  };

  const clearAllNotifications = async (e) => {
    e.stopPropagation();
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const snap = await getDocs(collection(db, `users/${uid}/notifications`));
    await Promise.all(snap.docs.map(d => deleteDoc(d.ref).catch(() => {})));
  };

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    updateOnlineStatus(); // Initial check

    // Check every 5 seconds
    const interval = setInterval(updateOnlineStatus, 5000);

    // Also listen to events
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  useEffect(() => {
    const checkTrial = async () => {
      if (!auth.currentUser || !subscriptionStatus) {
        setShowTrialBanner(false);
        return;
      }

      if (subscriptionStatus.status === 'trial') {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const accessEnd = new Date(data.accessEndDate);
          const now = new Date();
          const diffTime = accessEnd - now;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays > 0) {
            setDaysLeft(diffDays);
            setShowTrialBanner(true);
          } else {
            // Trial expired
            setDaysLeft(0);
            setShowTrialBanner(true);
          }
        }
      } else if (subscriptionStatus.status === 'expired' || subscriptionStatus.status === 'canceled') {
        setDaysLeft(-1); // Special indicator for expired/lapsed
        setShowTrialBanner(true);
      } else if (subscriptionStatus.status === 'past_due') {
        // For past_due, calculate days until access actually ends
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const accessEnd = new Date(data.accessEndDate);
          const now = new Date();
          const diffTime = accessEnd - now;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays > 0) {
            setDaysLeft(diffDays);
            setShowTrialBanner(true);
          } else {
            // Access has actually expired
            setDaysLeft(-1);
            setShowTrialBanner(true);
          }
        }
      } else {
        setShowTrialBanner(false);
      }
    };

    checkTrial();
  }, [subscriptionStatus]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    auth.signOut();
    navigate('/');
  };

  const menuItems = [
    { label: 'Settings', path: '/settings', icon: 'settings' },
    { label: 'Account', path: '/account', icon: 'account_circle' },
    { label: 'Help', path: '/help', icon: 'help' },
    { label: 'Log Out', action: handleLogout, icon: 'logout' },
  ];

  const getSyncIcon = () => {
    if (!isOnline) return 'cloud_off';
    if (syncStatus === 'syncing') return 'sync';
    return 'cloud';
  };

  const getSyncClass = () => {
    return isOnline && syncStatus !== 'syncing' ? 'sync-icon-online' : 'sync-icon-offline';
  };

  const getBannerColor = () => {
    if (subscriptionStatus.status === 'past_due') return '#ff6b35'; // Orange for payment issues
    if (daysLeft === -1) return '#dc2626'; // Red for expired/lapsed
    if (daysLeft < 5) return '#dc2626'; // Red for <5 days
    if (daysLeft <= 15) return '#ff6b35'; // Orange for 5-15 days
    return '#2563eb'; // Blue for >15 days
  };

  const getBannerBorderColor = () => {
    if (subscriptionStatus.status === 'past_due') return '#e55a2b'; // Orange border for payment issues
    if (daysLeft === -1) return '#b91c1c';
    if (daysLeft < 5) return '#b91c1c';
    if (daysLeft <= 15) return '#e55a2b';
    return '#1d4ed8';
  };

  const getBannerMessage = () => {
    if (daysLeft === -1) {
      if (subscriptionStatus.status === 'expired') {
        return 'Your trial has expired.';
      } else {
        return 'Your account is on hold.';
      }
    }
    if (subscriptionStatus.status === 'past_due') {
      return 'There has been a problem with your payment method.';
    }
    const planName = subscriptionStatus.plan ? subscriptionStatus.plan.charAt(0).toUpperCase() + subscriptionStatus.plan.slice(1) : 'Pro';
    return `Your ${planName} trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}.`;
  };

  const getBannerButtonText = () => {
    if (subscriptionStatus.status === 'past_due') {
      return 'Fix Payment Method';
    }
    if (daysLeft === -1) {
      if (subscriptionStatus.status === 'expired') {
        return 'Choose Plan';
      } else {
        return 'Fix Payment Method';
      }
    }
    return 'Choose Plan';
  };

  const handleBannerAction = async () => {
    if (subscriptionStatus.status === 'past_due' || (daysLeft === -1 && subscriptionStatus.status !== 'expired')) {
      // For past_due or lapsed payments, redirect to Stripe portal to fix payment
      setBannerLoading(true);
      try {
        const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
        const result = await createPortalLink({ returnUrl: window.location.href });
        window.location.assign(result.data.url);
      } catch (err) {
        console.error('Error opening subscription portal:', err);
        alert('Unable to open billing portal. Please try again later.');
        setBannerLoading(false);
      }
    } else {
      // For active trials or expired trials, show plan selection
      setShowPlanModal(true);
    }
  };

  return (
    <>
      <header className="header">
        <img
          src="/logo.png"
          alt="Logo"
          className="header-logo"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        />
        <div
          className="header-title"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', flex: 'none' }}
        >
          Hot Setup
        </div>
        <div style={{ flex: 1 }}></div>
        <div className="header-sync" title={syncStatus}>
          <span className={`material-icons ${getSyncClass()}`}>
            {getSyncIcon()}
          </span>
        </div>
        <div ref={notifRef} className="header-notif-wrapper" style={{ position: 'relative' }}>
          <button onClick={openNotifications} className="notif-button" title="Notifications">
            <span className="material-icons">notifications</span>
            {unreadCount > 0 && <span className="notif-count">{unreadCount}</span>}
          </button>
          {showNotifs && (
            <div className="notif-dropdown">
              <div className="notif-header">
                <span>Notifications</span>
                {notifications.length > 0 && (
                  <button className="notif-clear-all" onClick={clearAllNotifications}>Clear all</button>
                )}
              </div>
              {notifications.length === 0 && <div className="notif-empty">No notifications</div>}
              {notifications.map(n => (
                <div
                  key={n.id}
                  className={`notif-item ${n.read ? '' : 'unread'} ${n.sessionId ? 'clickable' : ''}`}
                  onClick={() => handleNotifClick(n)}
                >
                  <div className="notif-body">
                    <span className="notif-message">{n.message}</span>
                    {(senderName(n) || n.timestamp) && (
                      <span className="notif-meta">
                        {senderName(n)}{senderName(n) && n.timestamp ? ' · ' : ''}{formatNotifTime(n.timestamp)}
                      </span>
                    )}
                  </div>
                  <button className="notif-clear-one" onClick={(e) => clearNotification(e, n)} title="Clear">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => setMenuOpen(!menuOpen)} className="header-menu-btn" title="Menu">
          <span className="material-icons">menu</span>
        </button>
        {menuOpen && (
          <div className="header-menu" ref={menuRef}>
            {menuItems.map((item, index) => (
              <p
                key={index}
                onClick={() => {
                  if (item.action) {
                    item.action();
                  } else {
                    navigate(item.path);
                  }
                  setMenuOpen(false);
                }}
              >
                <span className="material-icons">{item.icon}</span>
                {item.label}
              </p>
            ))}
          </div>
        )}
      </header>

      {showTrialBanner && (
        <div style={{
          backgroundColor: getBannerColor(),
          color: 'white',
          padding: '8px 20px',
          textAlign: 'center',
          fontSize: '14px',
          borderBottom: `1px solid ${getBannerBorderColor()}`
        }}>
          {getBannerMessage()}{' '}
          <button
            onClick={() => handleBannerAction()}
            style={{
              background: 'none',
              border: '1px solid white',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: bannerLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              marginLeft: '10px',
              opacity: bannerLoading ? 0.7 : 1
            }}
            disabled={bannerLoading}
          >
            {bannerLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div className="spinner" style={{ width: '12px', height: '12px', border: '2px solid #ffffff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                Loading...
              </div>
            ) : (
              getBannerButtonText()
            )}
          </button>
        </div>
      )}

      <PlanSelectionModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        subscriptionStatus={subscriptionStatus}
      />
    </>
  );
}

export default Header;